/**
 * api.ts — Cliente HTTP fino para a API do backend (/api).
 *
 * Em dev o Vite faz proxy de /api -> http://localhost:8002 (ver vite.config).
 * Sempre envia o cookie de sessão (credentials: 'include').
 */

const BASE = '/api';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** O texto do erro, venha ele como for.
 *
 * ⛔ **O `detail` do FastAPI NEM SEMPRE É TEXTO.** Num 400/403/404 que a gente
 * levanta à mão ele é uma frase; num **422 de validação** ele é uma LISTA DE
 * OBJETOS (`[{loc, msg, type, input}]`), e era isso que a tela mostrava:
 *
 *     [object Object]
 *
 * Achado por ele em 13/09/2026, tentando vincular uma OS a uma autorização. E o
 * estrago não foi só a feiura: a mensagem ilegível **escondeu o defeito de
 * verdade** por trás dela (o `Literal` do schema preso num nome morto). Erro que
 * não se lê é defeito que não se acha.
 *
 * ⚠️ Vale para o app INTEIRO — qualquer 422, em qualquer tela, caía aqui. */
function mensagemDoErro(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const d = (data as Record<string, unknown>).detail ?? (data as Record<string, unknown>).error;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    /* Um 422 pode reclamar de vários campos. Cada linha vira "campo: o que
       está errado" — o nome do campo é o ÚLTIMO pedaço do `loc`, porque os
       primeiros são só "body"/"query" e não dizem nada a quem está na tela. */
    const linhas = d
      .map((e) => {
        if (!e || typeof e !== 'object') return null;
        const item = e as { loc?: unknown[]; msg?: unknown };
        const campo = Array.isArray(item.loc) ? String(item.loc[item.loc.length - 1] ?? '') : '';
        const msg = typeof item.msg === 'string' ? item.msg : '';
        if (!msg) return null;
        return campo && campo !== 'body' ? `${campo}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (linhas.length) return linhas.join(' · ');
  }
  return null;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
    ...options,
  });

  if (!res.ok) {
    let mensagem = 'Erro inesperado. Tente novamente.';
    try {
      const data = await res.json();
      mensagem = mensagemDoErro(data) ?? mensagem;
    } catch {
      /* corpo sem JSON — mantém mensagem padrão */
    }
    throw new ApiError(res.status, mensagem);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  /**
   * Como o `get`, mas devolve TAMBÉM o total de registros do cabeçalho
   * `X-Total-Registros` — o que a barra de paginação precisa para saber quantas
   * páginas existem.
   *
   * Existe separado do `get` de propósito: o total vem no CABEÇALHO justamente
   * para não mudar o corpo da resposta, e quem não pagina não deve nem tomar
   * conhecimento disso.
   */
  getPaginado: async <T>(
    path: string,
  ): Promise<{ itens: T; total: number; cabecalhos: Headers }> => {
    const res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      let mensagem = 'Erro inesperado. Tente novamente.';
      try {
        const data = await res.json();
        mensagem = data.detail ?? data.error ?? mensagem;
      } catch {
        /* corpo sem JSON */
      }
      throw new ApiError(res.status, mensagem);
    }
    const itens = (await res.json()) as T;
    // Sem o cabeçalho (rota ainda não paginada), o total é o tamanho da lista.
    const cab = res.headers.get('X-Total-Registros');
    const total = cab != null ? Number(cab) : (Array.isArray(itens) ? itens.length : 0);
    // `cabecalhos` vai junto porque algumas listas mandam mais que o total —
    // Despesas manda a SOMA dos valores (`X-Soma-Valor`), que a tela paginada
    // não pode mais calcular sozinha.
    return { itens, total, cabecalhos: res.headers };
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Upload multipart (FormData) — não define Content-Type (o browser cuida do boundary). */
  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      let mensagem = 'Falha no upload.';
      try {
        const data = await res.json();
        mensagem = data.detail ?? data.error ?? mensagem;
      } catch {
        /* sem corpo JSON */
      }
      throw new ApiError(res.status, mensagem);
    }
    return res.json() as Promise<T>;
  },
};
